import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isElectron } from '../../api';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { getRemoteClientStore } from '../../api/stores/remoteClientStores';
import { getMediaTrackFromIdAsPromise, refreshDisplayShadowcamStreams } from '../../api/modules/managers/mediaManager';
import globalFrame from '../../../assets/global-frame.png';
import config from '../../config.json';
import { mediaCoordinator, sessionCoordinator } from '../../coordinators/init';
import { USE_BEACON_COORDINATOR, USE_MEDIA_COORDINATOR, USE_SESSION_COORDINATOR } from '../../config/featureFlags';
import { PreviewCaptureSource } from './PreviewCaptureSource';

const GREEN_DOMINANCE_THRESHOLD = 0.65;
const PREVIEW_HEALTH_CHECK_MS = 3_000;
const PREVIEW_RECOVERY_COOLDOWN_MS = 10_000;

function isGreenGarbageFrame(imageData: ImageData): boolean {
  const { data, width, height } = imageData;
  let greenDominant = 0;
  let count = 0;
  for (let y = 0; y < height; y += 4) {
    for (let x = 0; x < width; x += 4) {
      const i = (y * width + x) * 4;
      const b = data[i];
      const g = data[i + 1];
      const r = data[i + 2];
      if (g > 160 && g > r + 35 && g > b + 35) {
        greenDominant += 1;
      }
      count += 1;
    }
  }
  return count > 0 && greenDominant / count >= GREEN_DOMINANCE_THRESHOLD;
}

export const Preview = () => {
  const context = useContext(PlaycastContext);
  const remoteStores = getRemoteClientStore();
  const previewRef = useRef<HTMLVideoElement>(null);
  const [showStaticImage, setShowStaticImage] = useState(false);
  const [staticImageContent, setStaticImageContent] = useState('');
  // Beacon's own Show/Hide already mounts this component. The host-rule flag stays
  // off so Pavilion does not decode a second preview, and must not blank beacon.
  const previewAllowed = USE_BEACON_COORDINATOR || (context.stateStores.hostRuleState.showVideoPreview ?? false);
  const [shouldShowPreview, setShouldShowPreview] = useState(previewAllowed);
  const [previewShadowcam, setPreviewShadowcam] = useState(context.stateStores.hostRuleState.videoPreviewCamera);
  const [isWindowMinimized, setIsWindowMinimized] = useState(false);
  const currentSourceRef = useRef('');
  const lastPreviewRecoveryRef = useRef(0);
  const previewHealthCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [captureSource] = useState(() => new PreviewCaptureSource(mediaCoordinator));
  const selectedDevice = context.stateStores.videoState.selectedDevice;

  const attachPreviewTrack = useCallback((mediaTrack: MediaStreamTrack) => {
    if (!previewRef.current) {
      return;
    }
    const currentStream = previewRef.current.srcObject as MediaStream | null;
    const currentTrack = currentStream?.getVideoTracks()[0];
    if (currentTrack?.id === mediaTrack.id) {
      currentSourceRef.current = mediaTrack.id;
      return;
    }
    console.debug('[Preview] Attaching video track', mediaTrack.label, mediaTrack.id);
    const mediaStream = new MediaStream();
    mediaStream.addTrack(mediaTrack);
    previewRef.current.srcObject = mediaStream;
    currentSourceRef.current = mediaTrack.id;
    setShowStaticImage(false);
    void previewRef.current.play().catch((err) => {
      console.warn('[Preview] play() failed:', err);
    });
  }, []);

  const clearPreviewSource = useCallback(() => {
    if (previewRef.current) previewRef.current.srcObject = null;
    currentSourceRef.current = '';
  }, []);

  const updateCoordinatorPreview = useCallback((track: MediaStreamTrack | null) => {
    const useStaticImage = context.stateStores.videoState.selectedDevice === '0';
    setShowStaticImage(useStaticImage);
    if (useStaticImage || track?.readyState !== 'live') {
      clearPreviewSource();
      return;
    }
    attachPreviewTrack(track);
  }, [context.stateStores.videoState.selectedDevice, clearPreviewSource, attachPreviewTrack]);

  const recoverPreviewCapture = useCallback(async () => {
    const now = Date.now();
    if (now - lastPreviewRecoveryRef.current < PREVIEW_RECOVERY_COOLDOWN_MS) {
      return;
    }

    if (USE_MEDIA_COORDINATOR && isElectron) {
      const hosting = USE_SESSION_COORDINATOR && sessionCoordinator.getStatus() === 'hosting';
      if (hosting) {
        console.debug('[Preview] Skipping green-frame restart — broadcast is active');
        currentSourceRef.current = '';
        if (previewRef.current) previewRef.current.srcObject = null;
        updateCoordinatorPreview(mediaCoordinator.getVideoTrack());
        return;
      }
      if (!captureSource.isShadowcamSource(selectedDevice)) return;
      lastPreviewRecoveryRef.current = now;
      const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
      console.warn('[Preview] Detected green garbage frame — restarting shadowcam capture');
      const track = await captureSource.recover(selectedDevice, previewCam);
      if (track && captureSource.matches(track, selectedDevice, previewCam)) {
        currentSourceRef.current = '';
        attachPreviewTrack(track);
      }
      return;
    }

    lastPreviewRecoveryRef.current = now;
    console.warn('[Preview] Detected green garbage frame — restarting shadowcam capture');
    void refreshDisplayShadowcamStreams();
  }, [attachPreviewTrack, captureSource, previewShadowcam, selectedDevice, updateCoordinatorPreview]);

  const setPreviewRef = useCallback(async () => {
    if (isElectron && context.stateStores.videoState.selectedDevice === '0') {
      clearPreviewSource();
      setShowStaticImage(true);
      return;
    }
    if (isElectron && USE_MEDIA_COORDINATOR) {
      const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
      const liveTrack = mediaCoordinator.getVideoTrack();
      // While broadcasting, the live capture belongs to the stream: show whatever it is rather
      // than re-capturing a preferred camera out from under every connected peer.
      const isBroadcasting = USE_SESSION_COORDINATOR && sessionCoordinator.getStatus() === 'hosting';
      if (isBroadcasting) {
        updateCoordinatorPreview(liveTrack);
        return;
      }
      if (
        liveTrack?.readyState === 'live' &&
        ((liveTrack.label?.includes(previewCam) ?? false)
          || captureSource.matches(liveTrack, selectedDevice, previewCam))
      ) {
        updateCoordinatorPreview(liveTrack);
        return;
      }
      clearPreviewSource();
      const coordinatorTrack = await captureSource.capture(selectedDevice, previewCam);
      if (coordinatorTrack && captureSource.matches(coordinatorTrack, selectedDevice, previewCam)) {
        attachPreviewTrack(coordinatorTrack);
        return;
      }
      updateCoordinatorPreview(liveTrack);
      return;
    }

    if (isElectron && context.stateStores.videoState.selectedDevice) {
      setShowStaticImage(false);

      const previewCam = previewShadowcam || config.PREVIEW_SHADOWCAM;
      let legacyDevice = selectedDevice;
      if (legacyDevice === 'display') {
        legacyDevice = `display-${previewCam}`;
      }
      if (legacyDevice === 'followMouse') {
        legacyDevice = `followMouse-${previewCam}`;
      }
      const mediaTrack = await getMediaTrackFromIdAsPromise(legacyDevice);
      if (mediaTrack) {
        attachPreviewTrack(mediaTrack);
      }
    } else {
      if (context.stateStores.videoState.selectedDevice === '0') {
        setShowStaticImage(true);
      } else {
        setShowStaticImage(false);
        const mediaStream = remoteStores.previewStreamState.currentState;
        if (mediaStream) {
          if (previewRef.current) {
            previewRef.current.srcObject = mediaStream;
            previewRef.current.load();
          }
        }
      }
    }
    // if not already playing, start playing
    if (previewRef.current && previewRef.current.paused) {
      if (previewRef.current.srcObject) {
        // await previewRef.current.play();
      }
    }
  }, [selectedDevice, captureSource, previewShadowcam, remoteStores.previewStreamState.currentState, attachPreviewTrack, clearPreviewSource, updateCoordinatorPreview]);

  useEffect(() => {
    if (!USE_MEDIA_COORDINATOR || !isElectron) return;
    // tracksChanged fires when the coordinator swaps the single capture every consumer shares —
    // including onto a different ShadowCam for a mobile guest. Filtering by camera label here
    // would leave the preview frozen on a stopped track for the rest of the broadcast.
    return mediaCoordinator.events.tracksChanged.addListener(({ videoTrack }) => {
      updateCoordinatorPreview(videoTrack);
    });
  }, [updateCoordinatorPreview]);

  useEffect(() => {
    setStaticImageContent(context.stateStores.staticImageState.staticImageBase64);
  }, [context.stateStores.staticImageState.staticImageBase64]);

  useEffect(() => {
    const listener = remoteStores.previewTrackState.addListener(() => {
      setPreviewRef();
    });
    setStaticImageContent(context.stateStores.staticImageState.staticImageBase64);
    return () => {
      remoteStores.previewTrackState.removeListener(listener);
    };
  }, [setPreviewRef]);

  useEffect(() => {
    setShouldShowPreview(
      USE_BEACON_COORDINATOR || context.stateStores.hostRuleState.showVideoPreview,
    );
    setPreviewShadowcam(context.stateStores.hostRuleState.videoPreviewCamera);
  }, [context.stateStores.hostRuleState]);

  const shadowcamCount = context.stateStores.videoState.shadowcams.length;

  useEffect(() => {
    setPreviewRef();
  }, [shouldShowPreview, previewShadowcam, selectedDevice, showStaticImage, setPreviewRef]);

  useEffect(() => {
    if (!isElectron || USE_MEDIA_COORDINATOR) return;
    void refreshDisplayShadowcamStreams();
  }, [shadowcamCount]);

  useEffect(() => {
    const listener = remoteStores.previewStreamState.addListener(() => {
      setPreviewRef();
    });
    return () => {
      remoteStores.previewStreamState.removeListener(listener);
    };
  }, [setPreviewRef]);

  // PERFORMANCE: Pause video preview when window is minimized
  // This eliminates 80-90% of idle CPU usage when minimized
  useEffect(() => {
    if (!isElectron) return;
    
    const handleWindowState = (state: string) => {
      if (state === 'minimized') {
        console.log('Window minimized - pausing preview video');
        setIsWindowMinimized(true);
        if (previewRef.current) {
          previewRef.current.pause();
        }
      } else if (state === 'normal' || state === 'maximized') {
        console.log('Window restored - resuming preview video');
        setIsWindowMinimized(false);
        if (previewRef.current && previewRef.current.srcObject) {
          previewRef.current.play().catch((err) => {
            console.warn('Failed to resume video:', err);
          });
        }
      }
    };

    // Listen for window state changes from Electron
    window.electron?.on('window-state-changed', handleWindowState);

    return () => {
      window.electron?.removeListener?.('window-state-changed', handleWindowState);
    };
  }, []);

  // Also pause/resume based on isWindowMinimized state
  useEffect(() => {
    if (previewRef.current) {
      if (isWindowMinimized) {
        previewRef.current.pause();
      } else if (previewRef.current.srcObject) {
        previewRef.current.play().catch((err) => {
          console.warn('Failed to play video:', err);
        });
      }
    }
  }, [isWindowMinimized]);

  useEffect(() => {
    if (!isElectron || !shouldShowPreview || showStaticImage || isWindowMinimized) {
      return;
    }

    const timer = window.setInterval(() => {
      const video = previewRef.current;
      if (!video || video.videoWidth === 0 || video.videoHeight === 0 || video.paused) {
        return;
      }

      if (!previewHealthCanvasRef.current) {
        previewHealthCanvasRef.current = document.createElement('canvas');
      }
      const canvas = previewHealthCanvasRef.current;
      const sampleWidth = 64;
      const sampleHeight = 36;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        return;
      }

      ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);
      const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
      if (isGreenGarbageFrame(imageData)) {
        void recoverPreviewCapture();
      }
    }, PREVIEW_HEALTH_CHECK_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [isWindowMinimized, recoverPreviewCapture, shouldShowPreview, showStaticImage]);

  return (
    shouldShowPreview &&
    (showStaticImage ? (
      <img
        id='preview-static-image'
        alt='static preview'
        className='aspect-video size-full max-h-full max-w-full bg-black object-cover'
        src={staticImageContent ? staticImageContent : globalFrame}
      />
    ) : (
      <video
        className='size-full bg-black object-contain'
        ref={previewRef}
        autoPlay
        muted
        playsInline
      />
    ))
  );
};
